import React, { useEffect } from 'react';
import { gikClassPrefix } from '@gik/core/constants';
import { UINotificationVariant, UISize } from '../types';
import classnames from 'classnames';
import FontIcon from '../FontIcon';

export interface INotificationProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Style variant to use
   */
  variant?: UINotificationVariant;

  /**
   * Size the component on a fixed scale
   */
  size?: UISize;

  /**
   * Make this notification closable
   */
  close?: boolean;
  clickThrough?: string;

  prepend?: React.ReactNode;
  append?: React.ReactNode;

  notificationId?: string;

  /**
   * Make this notification closable
   */
  autoClose?: boolean;

  /**
   * Number in milliseconds the notification should autoclose (if autoclose is set to true)
   */
  autoCloseTime?: number;

  theme?: string;

  onClose?(notificationId: string): void;
  onClickThrough?(): void;
}

/**
 * Exposes high level ui methods for common UI functionalities.
 */
export default function Notification({
  notificationId,
  children,
  variant = 'default',
  size = 'base',
  theme,
  close = true,
  clickThrough,
  onClickThrough,
  className,
  append,
  prepend,
  autoClose = true,
  autoCloseTime = 5000,
  onClose,
  ...otherProps
}: INotificationProps): React.ReactElement {
  const blockName = `${gikClassPrefix}-notification`;
  let closeTimeout: ReturnType<typeof setTimeout>;

  function resetTime() {
    clearTimeout(closeTimeout);
    closeTimeout = setTimeout(_close, autoCloseTime);
  }

  const resetTimeCallback = React.useCallback(resetTime, []);

  useEffect(() => {
    if (autoClose) {
      resetTimeCallback();
    }
  }, [autoClose, resetTimeCallback]);

  function _close() {
    if (onClose) onClose(notificationId);
  }

  function handleMouseEnter() {
    clearTimeout(closeTimeout);
  }

  function handleMouseLeave() {
    if (autoClose) resetTime();
  }

  const blockClasses: string = classnames([
    { [blockName]: true },
    { [`${blockName}--${theme}`]: theme },
    { [`${blockName}--${variant}`]: variant },
    { [`${blockName}--size-${size}`]: size },
    { [`${blockName}--click-through`]: clickThrough },
    className || '',
  ]);

  function handleClose(event: React.MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    _close();
  }

  function handleClickThrough() {
    if (close) {
      _close();
    }
    onClickThrough();
  }

  const content = (
    <>
      {prepend && <div className={`${blockName}__prepend`}>{prepend}</div>}
      <div className={`${blockName}__content-wrapper`}>
        <div className={`${blockName}__content`}>{children}</div>
        {append && <div className={`${blockName}__append`}>{append}</div>}
      </div>
      {close && (
        <div className={`${blockName}__close`} onClick={handleClose}>
          <FontIcon name="close" />
        </div>
      )}
      {!close && clickThrough && (
        <div className={`${blockName}__close`} onClick={_close}>
          <FontIcon name="chevronRight" />
        </div>
      )}
    </>
  );

  return (
    <div className={blockClasses} {...otherProps} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {!clickThrough && content}
      {clickThrough && <a onClick={handleClickThrough}>{content}</a>}
    </div>
  );
}
